/**
 * Color tokens. The hyphenated keys (`oh-orange`, …) are the wire format used by
 * `palette.js` → MUI theme. The camelCase named exports below are the preferred
 * import shape for new styled-components / inline JS code:
 *
 *   import { colors } from 'app/theme/colors'        // new code
 *   import colorsLegacy from 'app/theme/colors'      // palette.js
 *
 * Plain .css files (index.css, slider.css, history.css, student.css, etc.)
 * consume the matching `--oh-*` CSS variables defined in src/index.css `:root`.
 * Keep that block in sync with the values here when adding/changing colors.
 */

// Preferred camelCase token names for new JS code.
export const colors = {
  // Core brand (matches values used by MUI palette + index.css)
  ohOrange: 'rgba(242, 94, 53, 1)',
  // Softer red used for error states. Matches index.css `--oh-red` so emitting
  // CSS vars from this file doesn't shift the look of `.error`, `Boldheading`,
  // etc. (The previous colors.js had `oh-red: '#ef5c34'` — identical to
  // orange — but nothing actually consumed it from JS.)
  ohRed: '#e87d7c',
  ohWhite: '#f5f5f5',
  ohOffWhite: '#f8f8f8',
  ohBlack: '#555555',
  ohBlackLight: '#717171',
  ohBlue: '#66b5de',
  ohTeal: '#95c48c',
  ohGreen: '#a3c996',
  ohDisabled: '#d3d3d3',
  ohBoxGray: '#fafafa',
  ohGameBlack: '#15202c',
  ohVlBlue: 'rgba(102, 181, 222, 0.1)',
  ohBorderGray: 'rgba(0, 0, 0, 0.05)',
  ohShadowGray: 'rgba(64, 64, 64, 0.3)',

  // NEW additions — previously hardcoded inline; consolidated here so future
  // refactors have one place to change them.

  // Pure white / black (index.css uses #fff/--white; here we expose semantic names)
  white: '#ffffff',
  black: '#000000',

  // Text on light surfaces (alpha-based on black). Each has a camelCase
  // `text*` semantic name for new JS code, and an `ohBlack*` alias whose
  // kebab form (`--oh-black-primary`, …) matches the CSS-variable names
  // existing styled-components and .css files already reference.
  textPrimary: 'rgba(0, 0, 0, 0.86)',
  ohBlackPrimary: 'rgba(0, 0, 0, 0.86)',
  textSecondary: 'rgba(0, 0, 0, 0.56)',
  ohBlackSecondary: 'rgba(0, 0, 0, 0.56)',
  textTertiary: 'rgba(0, 0, 0, 0.36)',
  ohBlackTertiary: 'rgba(0, 0, 0, 0.36)',

  // Surface grays beyond ohWhite/ohOffWhite. Replaces #ccc, #666, #999 sprinkled inline.
  grayLight: '#e5e5e5',
  grayMedium: '#cccccc',
  grayDark: '#999999',

  // Brand orange variants used by index.css (#de532e, #f7eee9, #fbf5ed) but
  // not previously surfaced as JS tokens.
  ohDarkOrange: '#de532e',
  ohLightOrange: '#f7eee9',
  ohLightCream: '#fbf5ed',

  // Data tables — was hardcoded #282e3c in history.css, student.css and as
  // sx={{backgroundColor: '#202020'}} inline in CartDetailsTable. Names
  // chosen so GlobalTokens emits --oh-table-header-bg / --oh-table-header-text
  // matching the existing usages in history.css and the index.css :root block.
  ohTableHeaderBg: '#282e3c',
  ohTableHeaderText: '#ffffff',

  // Border / divider tokens (was rgba(0,0,0,0.12) inline). `borderDefault` and
  // `ohGray` share a value: the former is the preferred semantic name for new
  // code; the latter mirrors `--oh-gray` in index.css for legacy CSS consumers.
  borderDefault: 'rgba(0, 0, 0, 0.12)',
  ohGray: 'rgba(0, 0, 0, 0.12)',

  // Semantic gray scale (named by purpose, not by shade — matches the design
  // call to use semantic naming over a numbered scale). Each was previously
  // a hardcoded one-off:
  //   surfaceMuted    → was '#F6F6F6'    (Attendance pillStyles background)
  //   borderMuted     → was '#B3B3B566'  (Attendance pillStyles border, translucent gray)
  //   borderHover     → was '#212121'    (input hover border, ~near-black)
  //   dividerSubtle   → was '#c2c2c2'    (line dividers in chat / etc.)
  surfaceMuted: '#F6F6F6',
  borderMuted: 'rgba(179, 179, 181, 0.4)',
  borderHover: '#212121',
  dividerSubtle: '#c2c2c2',

  // Alpha sibling of ohOrange used for selected-state borders. Was '#EF5D3466'
  // in Attendance pillStyles — collapsed to a derived alpha of the canonical
  // ohOrange per the orange-shade design call.
  ohOrangeBorder: 'rgba(242, 94, 53, 0.4)',

  // Yellow brand accent (#f3c520) used by index.css confirm-button + button-alfred.
  ohYellow: '#f3c520',

  // Categorical 9-step brand palette + 0.1-alpha tints.
  ohBrand1: 'rgba(242, 183, 5, 1)',
  ohBrandVl1: 'rgba(242, 183, 5, 0.1)',
  ohBrand2: 'rgba(140, 86, 55, 1)',
  ohBrandVl2: 'rgba(140, 86, 55, 0.1)',
  ohBrand3: 'rgba(132, 108, 217, 1)',
  ohBrandVl3: 'rgba(132, 108, 217, 0.1)',
  ohBrand4: 'rgba(191, 99, 105, 1)',
  ohBrandVl4: 'rgba(191, 99, 105, 0.1)',
  ohBrand5: 'rgba(147, 204, 88, 1)',
  ohBrandVl5: 'rgba(147, 204, 88, 0.1)',
  ohBrand6: 'rgba(94, 174, 1, 1)',
  ohBrandVl6: 'rgba(94, 174, 1, 0.1)',
  ohBrand7: 'rgba(11, 158, 217, 1)',
  ohBrandVl7: 'rgba(11, 158, 217, 0.1)',
  ohBrand8: 'rgba(255, 118, 107, 1)',
  ohBrandVl8: 'rgba(255, 118, 107, 0.1)',
  ohBrand9: 'rgba(68, 137, 171, 1)',
  ohBrandVl9: 'rgba(68, 137, 171, 0.1)',

  // Shadows
  ohDefaultShadow: '0px 8px 16px rgba(0, 0, 0, 0.05)'
}

// camelCase → kebab-case. Used by both the legacy palette map (`oh-orange`)
// and the CSS-variable emitter (`--oh-orange`).
const toKebab = (s) => s.replace(/([A-Z]|[0-9]+)/g, '-$1').toLowerCase()

// Legacy hyphen-keyed map. Read by palette.js to build the MUI theme.
// Derived from `colors` above so we only ever change values in one place.
const legacyMap = Object.entries(colors).reduce((acc, [name, value]) => {
  acc[toKebab(name).replace(/^-/, '')] = value
  return acc
}, {})

/**
 * CSS-variable declarations string. Mounted at app root via
 * `<GlobalTokens/>` (app/theme/GlobalTokens.js) so plain .css files
 * (slider.css, history.css, …) and any styled-components written as
 * `var(--oh-orange)` resolve to the SAME values as `colors.ohOrange`.
 *
 * The duplicated `:root { … }` block in index.css remains for now as a
 * cheap fallback during the initial paint before React mounts; once we're
 * confident the GlobalStyle order is correct, that block can be deleted.
 */
export const cssVariables = Object.entries(colors)
  .map(([name, value]) => `  --${toKebab(name).replace(/^-/, '')}: ${value};`)
  .join('\n')

export default legacyMap
